import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { injectIntl, FormattedMessage } from 'react-intl';
import { parseISO } from 'date-fns';
import { format } from 'date-fns-tz';
import enUS from 'date-fns/locale/en-US';
import { Grid, Dimmer, Loader, Segment } from '../../../components';
import withHostContext, {
  trackClickTypes,
  trackingLocations,
  trackView,
} from '../../../context/host/consumer';
import VirtualVisitUrl from './VirtualVisitUrl';
import './styles.css';
import messages from './messages';
import { transformDeprecatedTimezones } from '../../../utils/transformDeprecatedTz';

class RepVisit extends React.PureComponent {
  static propTypes = {
    intl: PropTypes.object,
    visit: PropTypes.object,
  };

  constructor(props) {
    super(props);
    this.state = {
      viewMore: false,
      registered: false,
      canRegister: true,
      loading: false,
    };
  }

  componentDidMount() {
    this.setState({ canRegister: this.canRegister(), registered: this.registered() });
  }

  registered = () => {
    return !!get(this.props, 'visit.student.studentId', false);
  };

  deadlinePassed = () => {
    return this.props.visit.passedDaysCutoff || this.props.visit.passedHoursCutoff;
  };

  canRegister = () => {
    const { visit } = this.props;
    const userRole = get(this.props.userData, 'userType', '') === 'STUDENT';
    const registrationMaxed =
      this.props.registrationStatus.studentMaximum > 0 &&
      this.props.registrationStatus.studentCurrent >=
        this.props.registrationStatus.studentMaximum;
    const deadlinePassed = this.deadlinePassed();
    const eventFull = visit.passedMaxAttendees;
    // Calculate if user can register for events in school
    const schoolRegister = !!(
      this.props.canSignupVisits & Math.pow(2, this.props.userData.gradeLevel)
    );

    return (
      userRole && !registrationMaxed && !deadlinePassed && !eventFull && schoolRegister
    );
  };

  register = () => {
    const { id } = this.props.visit;
    trackView(this.props.hostContext, {
      type: trackClickTypes.HUBS_VIEW,
      location: `${trackingLocations.VISITS_REGISTER}_${id}`,
    });
    this.setState({ loading: true });
    this.props
      .register(id)
      .then(() => {
        this.setState({
          registered: true,
          loading: false,
          canRegister: this.canRegister(),
        });
      })
      .catch();
  };

  cancelRegistration = () => {
    const { id } = this.props.visit;
    trackView(this.props.hostContext, {
      type: trackClickTypes.HUBS_VIEW,
      location: `${trackingLocations.VISITS_CANCEL}_${id}`,
    });
    this.setState({ loading: true });
    this.props
      .cancelRegistration(id)
      .then(() => {
        this.setState({
          registered: false,
          loading: false,
          canRegister: this.canRegister(),
        });
      })
      .catch();
  };

  formatTimezone = (visitDate, timezoneName) => {
    let timezone = '';
    if (!isEmpty(timezoneName)) {
      try {
        timezone = format(visitDate, 'zzz', {
          timeZone: transformDeprecatedTimezones(timezoneName),
          locale: enUS,
        });
        timezone = ` ${timezone}`;
      } catch (e) {}
    }
    return timezone;
  };

  prepareRender = () => {
    const { intl, visit, timezoneName } = this.props;
    const { viewMore, canRegister, registered } = this.state;
    const deadlinePassed = this.deadlinePassed();
    const visitDate = parseISO(visit.visitDate);
    const dayOfWeek = format(visitDate, 'eeee');
    const day = format(visitDate, 'MMM d');
    const startHour = format(visitDate, 'h:mm aa');
    const timezone = this.formatTimezone(visitDate, timezoneName);
    return {
      intl,
      visit,
      timezoneName,
      viewMore,
      canRegister,
      registered,
      deadlinePassed,
      visitDate,
      dayOfWeek,
      day,
      startHour,
      timezone,
    };
  };

  firstRow() {
    const {
      intl,
      visit,
      viewMore,
      canRegister,
      registered,
      deadlinePassed,
      dayOfWeek,
      day,
      startHour,
      timezone,
    } = this.prepareRender();
    return (
      <Grid.Row className="first-row">
        <Grid.Column computer={3} mobile={12} tablet={16}>
          <Grid>
            <Grid.Row>
              <Grid.Column className="visit-date-column" only="computer" width={15}>
                <div className="visit-date" data-cy="visit-date">
                  <div>{dayOfWeek}</div>
                  <div className="day">{day}</div>
                  <div className="hour">
                    {startHour}
                    {timezone}
                  </div>
                </div>
              </Grid.Column>
              <Grid.Column only="tablet mobile" mobile={15} tablet={16}>
                <div className="visit-date" data-cy="visit-date">
                  <div>
                    {dayOfWeek}, {day}
                  </div>
                  <div className="hour">
                    {startHour}
                    {timezone}
                  </div>
                </div>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Grid.Column>
        <Grid.Column width={6}>
          <Grid className="visit-column-grid">
            <Grid.Row>
              <Grid.Column>
                <div className="visit-name" data-cy="visit-name">
                  <FormattedMessage
                    {...messages.event}
                    values={{
                      rep: visit.representative,
                      collegeName: visit.college.name,
                    }}
                  />
                </div>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column data-cy="visit-title">
                <Grid>
                  <Grid.Row>
                    <Grid.Column>
                      <div className="visit-title">{visit.visitName}</div>
                    </Grid.Column>
                  </Grid.Row>
                </Grid>
                <Grid>
                  <Grid.Row>
                    <Grid.Column only="tablet computer">
                      {visit.fcComments && (
                        <div
                          className="visit-view more"
                          onClick={() => {
                            this.setState({ viewMore: !viewMore });
                          }}
                          data-cy="visit-view-toggle-more"
                        >
                          {!viewMore ? intl.formatMessage(messages.moredetails) : ''}
                        </div>
                      )}
                    </Grid.Column>
                  </Grid.Row>
                </Grid>{' '}
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Grid.Column>
        <Grid.Column computer={4} tablet={5} mobile={12}>
          <Grid>
            <Grid.Row>
              <Grid.Column width={12} className="visit-location">
                <div data-cy="visit-location">
                  <span className="visit-location-name">{visit.location}</span>
                  <VirtualVisitUrl visit={visit} />
                </div>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column only="mobile">
                {visit.fcComments && (
                  <div
                    className="visit-view more"
                    onClick={() => {
                      this.setState({ viewMore: !viewMore });
                    }}
                    data-cy="visit-view-toggle-more"
                  >
                    {!viewMore ? intl.formatMessage(messages.moredetails) : ''}
                  </div>
                )}
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Grid.Column>
        <Grid.Column width={2} only="tablet computer">
          {!deadlinePassed && (
            <React.Fragment>
              {canRegister && !registered && (
                <div
                  className="visit-button"
                  data-cy="visit-signup"
                  onClick={this.register}
                >
                  <FormattedMessage {...messages.signup} />{' '}
                </div>
              )}
              {registered && (
                <div
                  className="visit-button"
                  data-cy="visit-cancel"
                  onClick={this.cancelRegistration}
                >
                  <FormattedMessage {...messages.cancel} />{' '}
                </div>
              )}
            </React.Fragment>
          )}
          {deadlinePassed && (
            <Grid className="visit-closed" data-cy="visit-closed">
              <Grid.Row>
                <Grid.Column>
                  <FormattedMessage {...messages.deadlinepassed} />
                </Grid.Column>
              </Grid.Row>
            </Grid>
          )}
        </Grid.Column>
      </Grid.Row>
    );
  }

  secondRow() {
    const {
      intl,
      visit,
      viewMore,
      canRegister,
      registered,
      deadlinePassed,
    } = this.prepareRender();
    return (
      <Grid.Row className="second-row">
        <Grid.Column width={3} only="computer" />
        {viewMore && (
          <Grid.Column width={10}>
            <div data-cy="visit-comment">
              <div className="visit-comment">{visit.fcComments}</div>
              {visit.fcComments && (
                <div
                  className="visit-view"
                  onClick={() => {
                    this.setState({ viewMore: !viewMore });
                  }}
                  data-cy="visit-view-toggle-less"
                >
                  {viewMore ? intl.formatMessage(messages.lessdetails) : ''}
                </div>
              )}
            </div>
          </Grid.Column>
        )}
        {!visit.fcComments && (
          <Grid.Column width={10}>
            <div data-cy="visit-nodetails" className="no-details">
              <FormattedMessage {...messages.nodetails} />
            </div>
          </Grid.Column>
        )}
        <Grid.Column only="mobile">
          {!deadlinePassed && (
            <React.Fragment>
              {canRegister && !registered && (
                <div
                  className="visit-button"
                  data-cy="visit-signup"
                  onClick={this.register}
                >
                  <FormattedMessage {...messages.signup} />{' '}
                </div>
              )}
              {registered && (
                <div
                  className="visit-button"
                  data-cy="visit-cancel"
                  onClick={this.cancelRegistration}
                >
                  <FormattedMessage {...messages.cancel} />{' '}
                </div>
              )}
            </React.Fragment>
          )}
          {deadlinePassed && <FormattedMessage {...messages.deadlinepassed} />}
        </Grid.Column>
      </Grid.Row>
    );
  }

  render() {
    const { loading } = this.state;
    return (
      <div className="grid-container" data-cy="rep-visit-row">
        <Dimmer.Dimmable as={Segment} dimmed={loading}>
          <Dimmer active={loading}>
            <Loader />
          </Dimmer>
          <Grid stackable>
            {this.firstRow()}
            {this.secondRow()}
          </Grid>
        </Dimmer.Dimmable>
      </div>
    );
  }
}

export default injectIntl(withHostContext(RepVisit));
